"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import AppLogo from "@/components/ui/AppLogo";

const navLinks = [
  { label: "Home", href: "/homepage" },
  { label: "Properties", href: "/properties" },
  { label: "Services", href: "/services" },
  { label: "Host Guides", href: "/pcb-host-guide" },
  { label: "Blog", href: "/blog" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
  { label: "Leave a Review", href: "/leave-a-review" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const isHome = pathname === "/homepage" || pathname === "/";
  const isDark = isHome && !scrolled;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-white/95 backdrop-blur-xl shadow-luxury border-b border-sand-dark"
            : isHome
            ? "bg-transparent" :"bg-navy/95 backdrop-blur-xl"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="flex items-center justify-between h-18 py-4">
            {/* Logo */}
            <Link href="/homepage" className="flex items-center gap-3 group">
              <AppLogo
                size={52}
                className="group-hover:scale-105 transition-transform duration-300"
              />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks?.map((link) => {
                const active = pathname === link?.href;
                return (
                  <Link
                    key={link?.href}
                    href={link?.href}
                    className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 group ${
                      active
                        ? scrolled
                          ? "text-navy" :"text-gold"
                        : scrolled
                        ? "text-navy/70 hover:text-navy" :"text-white/80 hover:text-white"
                    }`}
                  >
                    {link?.label}
                    <span
                      className={`absolute bottom-1 left-4 right-4 h-0.5 rounded-full transition-all duration-300 ${
                        active ? "opacity-100 scale-x-100" : "opacity-0 scale-x-0 group-hover:opacity-60 group-hover:scale-x-100"
                      } ${scrolled ? "bg-gold" : "bg-gold"}`}
                    />
                  </Link>
                );
              })}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center gap-3">
              <Link
                href="/owner-dashboard"
                className={`text-xs font-medium px-3 py-2 rounded-lg transition-all duration-300 ${
                  scrolled ? "text-navy/50 hover:text-navy/80" : "text-white/40 hover:text-white/60"
                }`}
              >
                Owner Portal
              </Link>
              <Link
                href="/contact"
                className={`text-sm font-medium px-4 py-2.5 rounded-full border transition-all duration-300 ${
                  scrolled
                    ? "border-navy/20 text-navy/60 hover:text-navy hover:border-navy/40"
                    : "border-white/20 text-white/60 hover:text-white hover:border-white/40"
                }`}
              >
                Get Free Quote
              </Link>
              <Link
                href="/book"
                className="btn-gold px-6 py-2.5 rounded-full text-sm font-semibold"
              >
                Book Now
              </Link>
            </div>

            {/* Mobile Toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle navigation menu"
              className={`lg:hidden p-2 rounded-lg transition-colors ${
                scrolled ? "text-navy hover:bg-sand-dark" : "text-white hover:bg-white/10"
              }`}
            >
              <div className="w-6 h-5 flex flex-col justify-between">
                <span className={`block h-0.5 rounded-full transition-all duration-300 ${scrolled ? "bg-navy" : "bg-white"} ${mobileOpen ? "rotate-45 translate-y-2.5" : ""}`} />
                <span className={`block h-0.5 rounded-full transition-all duration-300 ${scrolled ? "bg-navy" : "bg-white"} ${mobileOpen ? "opacity-0 scale-x-0" : ""}`} />
                <span className={`block h-0.5 rounded-full transition-all duration-300 ${scrolled ? "bg-navy" : "bg-white"} ${mobileOpen ? "-rotate-45 -translate-y-2.5" : ""}`} />
              </div>
            </button>
          </div>
        </div>
      </header>
      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-400 ${
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className="absolute inset-0 bg-navy-dark/90 backdrop-blur-lg"
          onClick={() => setMobileOpen(false)}
        />
        <div
          className={`absolute top-0 right-0 h-full w-80 bg-navy shadow-luxury-lg transition-transform duration-400 ease-smooth ${
            mobileOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex flex-col h-full pt-24 px-8 pb-8">
            <nav className="flex flex-col gap-1 flex-1">
              {navLinks?.map((link, i) => {
                const active = pathname === link?.href;
                return (
                  <Link
                    key={link?.href}
                    href={link?.href}
                    className={`flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-medium transition-all duration-300 ${
                      active
                        ? "bg-gold/20 text-gold border border-gold/30" :"text-white/70 hover:text-white hover:bg-white/10"
                    }`}
                    style={{ animationDelay: `${i * 60}ms` }}
                  >
                    {link?.label}
                    {active && (
                      <span className="ml-auto w-1.5 h-1.5 rounded-full bg-gold" />
                    )}
                  </Link>
                );
              })}
            </nav>

            <div className="border-t border-white/10 pt-6 mt-6 flex flex-col gap-3">
              <Link
                href="/book"
                className="btn-gold w-full py-3.5 rounded-xl text-center font-semibold"
              >
                Book Now
              </Link>
              <Link
                href="/contact"
                className="text-center text-white/60 text-sm py-2 hover:text-white transition-colors"
              >
                Get Free Quote
              </Link>
              <Link
                href="/owner-dashboard"
                className="text-center text-white/40 text-sm py-2 hover:text-white/60 transition-colors"
              >
                Owner Portal
              </Link>
            </div>
          </div>
        </div>
      </div>
      {/* Sticky CTA (mobile, appears on scroll) */}
      <div
        className={`fixed bottom-6 right-6 z-50 lg:hidden transition-all duration-500 ${
          scrolled ? "translate-y-0 opacity-100" : "translate-y-16 opacity-0"
        }`}
      >
        <Link
          href="/book"
          className="btn-gold sticky-cta flex items-center gap-2 px-5 py-3.5 rounded-full shadow-gold-lg font-semibold text-sm"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Book Now
        </Link>
      </div>
    </>
  );
}